import React from 'react';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import slugify from 'slugify';
import { useParams } from 'react-router';

import { OmaLummeApp } from '../../omaLummeContext';

import CurrentNews from './CurrentNews';
import Loading from '../Common/Loading';
import Box from '../Common/Box';
import BackButton from '../Common/BackButton';
import SidebarLiftup from './SidebarLiftup';
import AnonHeader from '../Common/AnonHeader';

// // Actions.
import { fetchNews } from '../../actions';
// // Utils.
import { formatHubspotBody } from '../../utils/hubspot';

const Article: React.FunctionComponent = () => {
  const [prevEndPoint, setPrevEndPoint] = React.useState('');
  const { t } = useTranslation();
  const { slug } = useParams<'slug'>();

  const dispatch = OmaLummeApp.useDispatch();
  const { users, news } = OmaLummeApp.useState();

  const { userId, endpoint } = users.activeUser;

  const [titleToDisplay, setTitleToDisplay] = React.useState<string>(`${t('common:general.loadingText')}`);
  const [bodyToDisplay, setBodyToDisplay] = React.useState<React.JSX.Element | undefined>(<Loading />);

  const article = news.find((article) => (typeof article?.title === 'string' ? slugify(article?.title).toLowerCase() === slug : false)) ?? undefined;

  React.useEffect(() => {
    async function fetchNewsAndArticles(): Promise<void> {
      setTitleToDisplay(`${t('common:general.loadingText')}…`);
      setBodyToDisplay(<Loading />);

      const fetchedNews = await fetchNews(endpoint, 999);

      dispatch(['changeNews', fetchedNews]);
    }
    if ((endpoint !== prevEndPoint || (article && slug !== slugify(article?.title).toLowerCase())) && news.length <= 6) {
      setPrevEndPoint(endpoint);
      fetchNewsAndArticles();
    }
  }, [userId, endpoint, prevEndPoint, news, slug, article, t]);

  React.useEffect(() => {
    if (article) {
      const { title, postBody, featuredImage, url } = article;

      let image = undefined;

      if (featuredImage) {
        image = <img src={featuredImage} alt="" />;
      }
      setTitleToDisplay(title);
      setBodyToDisplay(
        <div>
          {image}
          {parse(formatHubspotBody(postBody, url))}
        </div>,
      );
    }
  }, [article]);

  const articleContent = (
    <div aria-live="polite">
      {article && (
        <>
          <h1 aria-live="polite">{titleToDisplay}</h1>
          {bodyToDisplay}
        </>
      )}
      {!article && <Loading />}
    </div>
  );

  return (
    <>
      <Box wrapper size={endpoint !== 'anon' ? 'double' : 'anon'}>
        <Box size={endpoint !== 'anon' ? 'twoThirds' : 'anon'}>
          {endpoint === 'anon' && <AnonHeader />}
          <BackButton />
          {articleContent}
        </Box>

        {endpoint !== 'anon' && (
          <Box size="third" noStyles>
            <Box>
              <SidebarLiftup />
            </Box>
          </Box>
        )}
      </Box>

      {news && endpoint !== 'anon' && (
        <Box>
          <h2 className="sub-heading">{t('news:article.mayIntrest')}</h2>
          <CurrentNews activeArticle={article} />
        </Box>
      )}
    </>
  );
};

export default Article;
